import React from 'react';
import { Helmet } from 'react-helmet-async';
import './TermsAndConditions.css';

const TermsAndConditions = () => {
  return (
    <div className="terms-page">
      <Helmet>
        <title>PayCIS Terms &amp; Conditions | CIS Payroll Software Agreement</title>
        <meta name="description" content="Read the PayCIS Terms & Conditions to understand your responsibilities and rights when using our CIS payroll software. Learn about account use, payment obligations, data security, and legal terms." />
        <meta property="og:title" content="PayCIS Terms & Conditions | CIS Payroll Software Agreement" />
      </Helmet>
      <div className="terms-container">
        <h1>PAYCIS – TERMS OF USE</h1>
        <p style={{ marginBottom: '30px', color: '#666', fontSize: '14px' }}>
          Last updated: 01/15/2026
        </p>
        
        <div className="terms-content">
          <section>
            <p style={{ marginBottom: '20px' }}>
              These Terms of Use ("Terms") govern your access to and use of the PayCIS platform ("PayCIS", "we", "us", "our"). By registering for, accessing, or using PayCIS, you agree to be bound by these Terms.
            </p>
            <p style={{ fontWeight: 'bold', marginBottom: '30px' }}>
              If you do not agree, you must not use the platform.
            </p>
          </section>

          <section>
            <h2>1. Definitions</h2>
            <p>
              <strong>"Contractor", "Employer", "You" or "User"</strong> means any person or organisation using PayCIS to engage, verify, or pay subcontractors under the Construction Industry Scheme (CIS).
            </p>
            <p>
              <strong>"Subcontractor"</strong> means an individual or business providing construction services to a Contractor.
            </p>
            <p>
              <strong>"Subcontractor Data"</strong> means personal data relating to a subcontractor, including identity, contact, tax, verification and payment information.
            </p>
            <p>
              <strong>"Services"</strong> means the PayCIS platform, website, and related functionality.
            </p>
            <p>
              <strong>"HMRC"</strong> means HM Revenue & Customs.
            </p>
          </section>

          <section>
            <h2>2. Scope of the Service</h2>
            <p>
              PayCIS provides an online platform to assist Contractors and Subcontractors with:
            </p>
            <ul>
              <li>CIS verification;</li>
              <li>payment processing and approvals;</li>
              <li>payslip generation;</li>
              <li>record keeping; and</li>
              <li>preparation and submission of CIS-related information to HMRC.</li>
            </ul>
            <p>
              PayCIS does not provide tax advice, legal advice, or employment status determinations.
            </p>
          </section>

          <section>
            <h2>3. Eligibility and Registration</h2>
            <p>You must:</p>
            <ul>
              <li>be legally capable of entering into binding contracts;</li>
              <li>provide accurate, complete, and up-to-date information during registration; and</li>
              <li>ensure that any user accounts you create are used only by authorised persons.</li>
            </ul>
            <p>
              You are responsible for all activity carried out under your account.
            </p>
          </section>

          <section>
            <h2>4. User Responsibilities</h2>
            <p>You agree to:</p>
            <ul>
              <li>use PayCIS only for lawful purposes;</li>
              <li>ensure that all information you submit is accurate and complete;</li>
              <li>comply with all applicable laws, including CIS rules and tax obligations; and</li>
              <li>maintain the confidentiality of your login credentials.</li>
            </ul>
            <p>
              You must not misuse, interfere with, or attempt to gain unauthorised access to the platform.
            </p>
          </section>

          <section>
            <h2>5. Data Protection and Roles</h2>
            <h3>5.1 Data Controller Status</h3>
            <p>
              For the purposes of data protection law:
            </p>
            <ul>
              <li>PayCIS acts as a data controller in relation to the operation of the platform and the facilitation of data sharing between users.</li>
              <li>Contractors act as independent data controllers in respect of Subcontractor Data they access via PayCIS.</li>
            </ul>
            
            <h3>5.2 Use of Subcontractor Data</h3>
            <p>Contractors agree to:</p>
            <ul>
              <li>Use Subcontractor Data solely for lawful CIS purposes, including verification, payment processing, record-keeping, and HMRC reporting;</li>
              <li>Keep Subcontractor Data secure and confidential, limiting access to authorised personnel only;</li>
              <li>Not copy, export, disclose, or reuse Subcontractor Data outside PayCIS except where required by law or HMRC;</li>
              <li>Implement appropriate technical and organisational measures to protect Subcontractor Data;</li>
              <li>Comply with the UK GDPR and Data Protection Act 2018; and</li>
              <li>Notify PayCIS without undue delay of any actual or suspected personal-data breach involving Subcontractor Data.</li>
            </ul>
          </section>

          <section>
            <h2>6. Connections Between Users</h2>
            <p>
              Subcontractor Data will only be shared with a Contractor once the subcontractor has explicitly accepted and acknowledged a connection within PayCIS confirming that their information may be shared for CIS and payment purposes.
            </p>
            <p>
              Contractors must not attempt to bypass this process or obtain subcontractor personal data by other means.
            </p>
            <p>
              Subcontractors may view and remove active connections via their account dashboard.
            </p>
          </section>

          <section>
            <h2>7. Payments and CIS Compliance</h2>
            <p>
              PayCIS facilitates payment workflows and CIS reporting but does not guarantee:
            </p>
            <ul>
              <li>the accuracy of information provided by users;</li>
              <li>the tax treatment applied by HMRC; or</li>
              <li>the timing of payments once authorised.</li>
            </ul>
            <p>Contractors remain solely responsible for:</p>
            <ul>
              <li>determining employment status;</li>
              <li>ensuring correct CIS deductions; and</li>
              <li>meeting all HMRC obligations.</li>
            </ul>
          </section>

          <section>
            <h2>8. HMRC Submissions</h2>
            <p>Where PayCIS submits information to HMRC on your behalf:</p>
            <ul>
              <li>submissions are based on the data you provide;</li>
              <li>you remain responsible for the correctness of that data; and</li>
              <li>PayCIS is not liable for penalties or interest arising from inaccurate or late submissions caused by user input.</li>
            </ul>
          </section>

          <section>
            <h2>9. Suspension and Termination</h2>
            <p>PayCIS may suspend or terminate access if you:</p>
            <ul>
              <li>breach these Terms;</li>
              <li>misuse Subcontractor Data;</li>
              <li>provide false or misleading information; or</li>
              <li>use the platform unlawfully.</li>
            </ul>
            <p>
              Upon termination, you must cease using the platform and comply with data deletion or retention obligations as required by law.
            </p>
          </section>

          <section>
            <h2>10. Data Retention on Termination</h2>
            <p>
              Upon termination of your account or removal of a subcontractor connection, you must delete or securely destroy Subcontractor Data obtained through PayCIS unless you are legally required to retain it (for example, for HMRC audit purposes).
            </p>
          </section>

          <section>
            <h2>11. Intellectual Property</h2>
            <p>
              All intellectual property rights in PayCIS, including software, branding, and content, remain the property of PayCIS or its licensors.
            </p>
            <p>
              You are granted a limited, non-exclusive, non-transferable licence to use the platform in accordance with these Terms.
            </p>
          </section>

          <section>
            <h2>12. Limitation of Liability</h2>
            <p>To the fullest extent permitted by law:</p>
            <ul>
              <li>PayCIS is not liable for indirect, incidental, or consequential losses;</li>
              <li>PayCIS is not responsible for errors arising from user-provided data; and</li>
              <li>PayCIS does not guarantee uninterrupted or error-free service.</li>
            </ul>
            <p>
              Nothing in these Terms limits liability for fraud or other liability that cannot be excluded by law.
            </p>
          </section>

          <section>
            <h2>13. Changes to the Service or Terms</h2>
            <p>
              PayCIS may update these Terms from time to time. Continued use of the platform after changes take effect constitutes acceptance of the updated Terms.
            </p>
          </section>

          <section>
            <h2>14. Governing Law</h2>
            <p>
              These Terms are governed by and construed in accordance with the laws of England and Wales. The courts of England and Wales shall have exclusive jurisdiction.
            </p>
          </section>

          <section>
            <h2>15. Privacy</h2>
            <p>
              Use of PayCIS is also governed by the PayCIS Privacy & Data Usage Notice, which forms part of these Terms.
            </p>
          </section>
        </div>
      </div>
    </div>
  );
};

export default TermsAndConditions;
